import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { INDUSTRIES } from '@shared/catalog'
import { MarketingShell, DemoStartError } from '../components/Layout'
import { HeroRibbon } from '../components/HeroRibbon'
import { Card, Chip } from '../ui'
import { api } from '../lib/api'

const CAPABILITIES = [
  {
    title: 'One inbox, every number',
    body: 'Connect regular WhatsApp numbers — no Business API approval, no template queue. Your whole team answers from a single shared view.',
  },
  {
    title: 'Groups are first-class',
    body: 'Most platforms only handle 1:1 chats. Wami treats group conversations as the primary surface, because that is where operations actually happen.',
  },
  {
    title: 'Tickets from conversations',
    body: 'Flag a message and it becomes a tracked ticket with an owner and an SLA. Push it to Freshdesk or Zoho Desk without leaving the thread.',
  },
  {
    title: 'Agents that do the work',
    body: 'A real MCP server Claude Desktop or any MCP client can connect to — it searches conversations, drafts and sends replies, opens and escalates tickets, and reacts to messages. Not a read-only integration: it acts on your workspace, not just reports on it.',
  },
  {
    title: 'Numbers that stay connected',
    body: "Every connected number is scored on its own connection stability and recent send success — not a fabricated ban-risk model, since WhatsApp exposes no such signal to any third party. Unstable numbers get flagged and sends get paced automatically, before a number goes dark on you.",
  },
  {
    title: 'Audit trails that hold up',
    body: 'Append-only logs, retention policies, and regulator-ready exports for teams that have to prove what was said and when.',
  },
  {
    title: 'Priced for where you are',
    body: 'Regional pricing, local currency, and tax handled at checkout. The same product costs what it should in your market.',
  },
]

const INTEGRATIONS = ['HubSpot', 'Zoho CRM', 'Zoho Desk', 'Freshdesk', 'Slack', 'Google Sheets', 'Zapier']

export function Landing() {
  const navigate = useNavigate()
  const startDemo = useMutation({
    mutationFn: api.startDemo,
    onSuccess: () => navigate({ to: '/app' }),
  })

  return (
    <MarketingShell>
      <section
        className="hero"
        style={{ paddingBlock: 'clamp(56px, 11vw, 132px)', minHeight: 'min(78vh, 760px)' }}
      >
        <HeroRibbon />
        <div className="hero-veil" />
        <div className="container stack gap-5">
          <div className="row">
            <Chip tone="assist">No Business API approval required</Chip>
          </div>
          {/* ch on the heading itself, so the measure resolves against the display size. */}
          <h1 className="display-large" style={{ maxWidth: '15ch' }}>
            Run your business on WhatsApp.
          </h1>
          <p className="body-large text-muted measure">
            Wami turns WhatsApp groups and chats into a real operations system: a shared inbox
            across every number, tickets with owners and SLAs, and agents that act on your data.
          </p>
          <div className="row gap-3 wrap" style={{ marginTop: '8px' }}>
            <Link to="/signup" className="btn btn-filled btn-large">
              Start 14-day free trial
            </Link>
            <button
              type="button"
              className="btn btn-outlined btn-large"
              onClick={() => startDemo.mutate()}
              disabled={startDemo.isPending}
            >
              {startDemo.isPending ? 'Loading demo…' : 'See a live demo'}
            </button>
          </div>
          <p className="body-small text-muted">
            No card required. No WhatsApp Business API approval, no template queue.{' '}
            <Link to="/pricing">See pricing for your region.</Link>
          </p>
          {startDemo.isError && <DemoStartError error={startDemo.error} />}
        </div>
      </section>

      <section
        className="section rule-top"
        style={{ background: 'var(--md-surface-container-low)' }}
      >
        <div className="container stack gap-6">
          <div className="stack gap-2">
            <span className="eyebrow">What you get</span>
            <h2 className="headline-large" style={{ maxWidth: '20ch' }}>
              Built for the way operations teams actually use WhatsApp
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
            }}
          >
            {CAPABILITIES.map((c) => (
              <Card key={c.title} variant="outlined">
                <div className="stack gap-2">
                  <h3 className="title-large">{c.title}</h3>
                  <p className="body-medium text-muted">{c.body}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="container section stack gap-6">
        <div className="stack gap-2">
          <span className="eyebrow">Configured for your sector</span>
          <h2 className="headline-large" style={{ maxWidth: '22ch' }}>
            Pick your industry and start from a working configuration
          </h2>
          <p className="body-large text-muted" style={{ maxWidth: '60ch' }}>
            Each industry arrives with a plan, capacity, and compliance modules already selected —
            based on what teams in that sector actually need on day one. Change anything you like.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '16px',
          }}
        >
          {INDUSTRIES.map((industry) => (
            <Link
              key={industry.id}
              to="/pricing"
              search={{ industry: industry.id }}
              className="selectable"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <div className="stack gap-2">
                <h3 className="title-large">{industry.name}</h3>
                <p className="body-medium text-muted">{industry.description}</p>
                <div className="row gap-2 wrap" style={{ marginTop: '4px' }}>
                  {industry.workflowTemplates.slice(0, 2).map((w) => (
                    <Chip key={w} tone="assist">
                      {w}
                    </Chip>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section
        className="section rule-top"
        style={{ background: 'var(--md-surface-container-low)' }}
      >
        <div className="container stack gap-5">
          <div className="stack gap-2">
            <span className="eyebrow">Connects to what you run</span>
            <h2 className="headline-medium">Your systems stay the source of truth</h2>
          </div>
          <div className="row gap-2 wrap">
            {INTEGRATIONS.map((name) => (
              <Chip key={name} tone="assist">
                {name}
              </Chip>
            ))}
          </div>
          <p className="body-medium text-muted" style={{ maxWidth: '60ch' }}>
            Plus a REST API, outbound webhooks, and an MCP server so your own agents can read and act
            on conversations directly.
          </p>
        </div>
      </section>

      <section className="container section stack gap-5 center" style={{ textAlign: 'center', alignItems: 'center' }}>
        <h2 className="headline-large" style={{ maxWidth: '18ch' }}>
          See what it costs before you sign up
        </h2>
        <p className="body-large text-muted" style={{ maxWidth: '52ch' }}>
          The pricing page runs the same engine that bills you. Pick your industry and region and the
          number is the number.
        </p>
        <div className="row gap-3 wrap center">
          <Link to="/pricing" className="btn btn-filled btn-large">
            Build your quote
          </Link>
          <Link to="/signup" className="btn btn-text btn-large">
            Or start the trial
          </Link>
        </div>
      </section>
    </MarketingShell>
  )
}
